import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Sun, Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "../context/AppContext";

const LINKS = [
  { to: "/field-faces", label: "Field Faces" },
  { to: "/artwork", label: "Artwork" },
  { to: "/exhibitions", label: "Exhibitions" },
  { to: "/rise", label: "RISE Art Show" },
  { to: "/the-collection", label: "The Collection" },
  { to: "/datos", label: "DATOS" },
  { to: "/collect", label: "Collect" },
  { to: "/news", label: "News" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { cartCount, setCartOpen, dark, setDark } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border" data-testid="main-nav">
        <div className="container-x flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-mark">
            <span className="w-8 h-8 bg-[#FF1A1A] flex items-center justify-center font-display font-black text-white text-sm">AM</span>
            <span className="font-display font-extrabold uppercase tracking-tight text-sm sm:text-base leading-none">
              Atuarra<br className="sm:hidden" /> McCaslin
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.slice(0, 7).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-xs font-display font-bold uppercase tracking-widest transition-colors duration-300 hover:text-[#FF1A1A] ${isActive ? "text-[#FF1A1A]" : "text-foreground/70"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle-button"
              aria-label="Toggle theme"
              className="w-10 h-10 flex items-center justify-center border border-border hover:border-[#FF1A1A] hover:text-[#FF1A1A] transition-colors duration-300"
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={() => setCartOpen(true)}
              data-testid="cart-drawer-toggle"
              aria-label="Open cart"
              className="relative w-10 h-10 flex items-center justify-center border border-border hover:border-[#FF1A1A] hover:text-[#FF1A1A] transition-colors duration-300"
            >
              <ShoppingBag size={16} />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 bg-[#FF1A1A] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
            <button onClick={() => navigate("/collect")} data-testid="nav-collect-cta" className="hidden md:inline-flex btn-red !py-2.5 !px-5 text-xs">
              Collect Artwork
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-border"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-background flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="container-x flex items-center justify-between h-16 border-b border-border">
              <span className="font-display font-extrabold uppercase">Menu</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="w-10 h-10 flex items-center justify-center border border-border">
                <X size={18} />
              </button>
            </div>
            <nav className="container-x py-8 flex flex-col gap-1 overflow-y-auto">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="font-display font-black uppercase text-3xl sm:text-4xl py-2 block hover:text-[#FF1A1A] transition-colors duration-300"
                  >
                    <span className="font-mono-x text-xs text-[#FF1A1A] mr-4">{String(i + 1).padStart(2, "0")}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
